import React, { useMemo } from 'react';
import { Headline, ListedLink } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { apartmentStore } from '@stores/apartmentStore';
import { contractStore } from '@stores/contractStore';
import { userStore } from '@stores/userStore';
import { getLinkIcon } from '@utils/link.utils';

import { IListedLinkList, ParsedEntry } from '../types';

import { filterConditionalEntries } from './ConditionalWrapper';

interface Props {
  data: ParsedEntry<IListedLinkList>;
}

export const LinkList = observer(({ data }: Props) => {
  const { links, id, title, variant } = useMemo(
    () => ({
      ...data,
      links: filterConditionalEntries(data.links, {
        deps: { contractStore, apartmentStore, userStore },
        predicate: (entry) => !!entry.url && !!entry.title,
      }),
    }),
    [data, contractStore.selectedContract, apartmentStore.selectedApartment]
  );

  // early-exit if there isn't any links for display
  if (!links.length) return null;

  return (
    <div id={id} className="flex w-full flex-col gap-3">
      {title && <Headline level={3}>{title}</Headline>}

      <div className="w-full">
        {links.map(({ id, url, icon, ...listedLink }) => (
          <ListedLink
            key={id}
            id={id}
            {...listedLink}
            variant={variant ?? listedLink.variant}
            linkComponent={Link}
            linkProps={{ to: url }}
            icon={icon?.icon}
            arrowIcon={getLinkIcon(url, 'chevronRight')}
          />
        ))}
      </div>
    </div>
  );
});
